import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from 'state/axios';
import { 
  format, 
  addDays, 
  compareAsc, 
  differenceInDays, 
  isSameDay, 
} from "date-fns";
import toast from 'react-hot-toast';
import { debounce } from 'lodash';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography, 
  CircularProgress, 
  FormControl, 
  InputLabel, 
  MenuItem, 
  Select,
} from '@mui/material';
import {
  AirplanemodeActive,
  FlightTakeoff,
  Timelapse,
  FlightClass,
  AccessTime,
  AccessTimeFilled,
} from '@mui/icons-material';
import { 
  LAYOUT_HEIGHT, 
  OTP_DELAY_MINUTES, 
  MAXIMUM_TAT_MINUTES,
  DIVERT_RETURN_STATUS,
  EXCLUDED_FLIGHT_STATUS,
} from 'state/constants';
import { timeStringToMinutes, 
  removeItemByValueImmutable,
  convertLunarRangeToSolarByYear,
  convertLunarToSolar,
  calcOTP,
  safeMin, 
  safeMax,
  safeAvg,
} from 'state/helpers';
import { countFlightsByField, 
  countFlightsStackedBarChart, 
  countBarLineFlightsByFieldAndDuration 
} from 'scenes/charts/utils';
import { calculateOTPByETDRange } from 'scenes/otpAnalytics/utils';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StatBox from 'components/StatBox';
import IndexBox from 'components/IndexBox';
// import AnalogClock from 'components/AnalogClock';
import ScrollToTop from 'components/ScrollToTop';
import PageFooter from 'components/PageFooter';
import FireworksComponent from 'components/Fireworks';
import PieChart from 'components/chartNivo/PieChart';
import StackedBarChart from 'components/chartNivo/StackedBarChart';
import BarLineChart from 'components/chartNivo/BarLineChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightDelayList from 'components/FlightDelayList';
import ShortTurnaroundCrewRoutes from 'components/ShortTurnaroundCrewRoutes';
import OtpByPorts from './OtpByPorts';


const VIEW_OPTIONS = [
  { value: 'flightsByFlightReg', label: 'Flight by Reg' },
  { value: 'dailyFlightsByFlightReg', label: 'Daily Flight by Reg' },
  { value: 'flightsByACType', label: 'Flight by AC Type' },
  { value: 'flightsByFlightDate', label: 'Flight by Date' },
  { value: 'flightsByDep', label: 'Flight by Port' },
  { value: 'flightsBySector', label: 'Flight by Sector' },
  { value: 'flightsByFlightType', label: 'Flight by Type' },
];

const computeChartData = (flights = [], timeMode, view, dateDiff) => {
  const flightDate = timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase';

  switch (view) {
    case 'flightsByFlightReg':
      return { flightsByFlightReg: countFlightsStackedBarChart(flights, 'flightReg') };
    case 'dailyFlightsByFlightReg':
      return { dailyFlightsByFlightReg: countBarLineFlightsByFieldAndDuration(flights, 'blockHours', 'flightReg', dateDiff) };
    case 'flightsByACType':
      return { flightsByACType: countFlightsByField(flights, 'acType', 'pie') };
    case 'flightsByFlightDate':
      return { flightsByFlightDate: countFlightsStackedBarChart(flights, flightDate) };
    case 'flightsByDep':
      return { flightsByDep: countFlightsStackedBarChart(flights, 'dep') };
    case 'flightsBySector':
      return { flightsBySector: countFlightsStackedBarChart(flights, 'sector') };
    case 'flightsByFlightType':
      return { flightsByFlightType: countFlightsByField(flights, 'flightType', 'pie') };
    default:
      return {};
  }
};

const MAX_PAX_PER_FLIGHT = 500;
const updateOTP = (otp, f) => {
  if (!DIVERT_RETURN_STATUS.includes(f.flightStatus)) {
    if (f.flightStatus !== 'Not yet departed') {
      otp.dep.flights++;
      if (f.depDelaysTime > OTP_DELAY_MINUTES) 
        otp.dep.delays++
      else 
        otp.dep.ontime++;
    }

    if (f.flightStatus === 'Arrived') {
      otp.arr.flights++;
      if (f.arrDelaysTime > OTP_DELAY_MINUTES) 
        otp.arr.delays++
      else 
        otp.arr.ontime++;
    }
  }
};

const calcIndicators = (flights = [], timeMode, dateDiff) => {
  let depFlightsTotal = 0;
  let blockTimeTotal = 0;
  let departedBlockTime = 0;
  let paxCount = 0;
  let paxCapacity = 0;
  const pax1flightList = [];

  const aircraftSet = new Set();
  const aircraftPerDayMap = new Map();
  const flightsPerDayMap = new Map();
  const otp = {
    dep: { flights: 0, delays: 0, ontime: 0 },
    arr: { flights: 0, delays: 0, ontime: 0 },
  };
  const otpByDayMap = new Map();
  const depDelaysTimeList = [];
  const arrDelaysTimeList = [];
  const blockHours1flightList = [];
  const scheduleTatList = [];
  const tatList = [];
  const taxiOutList = [];
  const taxiInList = [];

  const dateKeyField = timeMode === 'UTC'
    ? 'flightDate'
    : 'flightDateLocalBase';

  flights?.forEach(f => {        
    /* --------- AIRCRAFT ------- */
    if (f.flightReg) {
      aircraftSet.add(f.flightReg);
    }
    
    /* -------- PER DAY MAPS -------- */
    const dateKey = f[dateKeyField];
    if (dateKey) {
      flightsPerDayMap.set(
        dateKey,
        (flightsPerDayMap.get(dateKey) || 0) + 1
      );

      if (!aircraftPerDayMap.has(dateKey)) {
        aircraftPerDayMap.set(dateKey, new Set());
      } else if (f.flightReg) {
        aircraftPerDayMap.get(dateKey).add(f.flightReg);
      }

      if (!otpByDayMap.has(dateKey)) {
        otpByDayMap.set(dateKey, {
          dep: { flights: 0, delays: 0, ontime: 0 },
          arr: { flights: 0, delays: 0, ontime: 0 },
        });
      }

      updateOTP(otpByDayMap.get(dateKey), f);
    }

    /* -------- TOTAL OTP ------- */
    updateOTP(otp, f);


    /* ---------- PAX ----------- */    
    const capacity = Math.min(f?.capacity || 0, MAX_PAX_PER_FLIGHT);
    const pax = Math.min(f?.noOfPax, capacity);
    if (pax > 0) {
      paxCount += Math.min(pax, capacity);
      paxCapacity += capacity;
      pax1flightList.push(pax);
    }

    /* ------- BLOCK TIME ------- */
    if (!DIVERT_RETURN_STATUS.includes(f.flightStatus)) {
      depFlightsTotal++;
      blockTimeTotal += timeStringToMinutes(f.blockTime);

      if (f.flightStatus !== 'Not yet departed') {
        departedBlockTime += timeStringToMinutes(f.blockTime);
      }
    }

    if (f.blockHours > 0) {
      blockHours1flightList.push(f.blockHours);
    }
    if (f.depDelaysTime > 0 && f.depDelaysTime > OTP_DELAY_MINUTES) {
      depDelaysTimeList.push(f.depDelaysTime);
    }
    if (f.arrDelaysTime > 0 && f.arrDelaysTime > OTP_DELAY_MINUTES) {
      arrDelaysTimeList.push(f.arrDelaysTime);
    }
    if (f.scheduleTat > 0 && f.scheduleTat < MAXIMUM_TAT_MINUTES) {
      scheduleTatList.push(f.scheduleTat);
    }
    if (f.tat > 0 && f.tat < MAXIMUM_TAT_MINUTES) {
      tatList.push(f.tat);
    }
    if (f.taxiOut > 0) {
      taxiOutList.push(f.taxiOut);
    }
    if (f.taxiIn > 0) {
      taxiInList.push(f.taxiIn);
    }
  });
  const flights1dayList = Array.from(flightsPerDayMap.values());  
  const depOTP1dayList = [];
  const arrOTP1dayList = [];

  otpByDayMap.forEach(day => {
    depOTP1dayList.push(calcOTP(day.dep.flights, day.dep.delays));
    arrOTP1dayList.push(calcOTP(day.arr.flights, day.arr.delays));
  });
  const depOTP = calcOTP(otp.dep.flights, otp.dep.delays);
  const arrOTP = calcOTP(otp.arr.flights, otp.arr.delays);

  const aircraftCount = aircraftSet.size;
  const aircraft1dayList = Array.from(aircraftPerDayMap.values()).map(
    set => set.size
  );
  const bh1AC1Day = (aircraftCount > 0 && dateDiff > 0) ? (blockTimeTotal / 60 / aircraftCount / dateDiff) : 0;
  

  return {
    aircraftCount,
    flightsTotal: depFlightsTotal,
    flownFlightsCount: otp.dep.flights,
    arrivedFlightsCount: otp.arr.flights,
    blockTime: blockTimeTotal / 60,
    flownBlockTime: departedBlockTime / 60,
    bh1AC1Day,

    depDelaysCount: otp.dep.delays,
    depOntimeCount: otp.dep.ontime,
    depOTP,
    arrDelaysCount: otp.arr.delays,
    arrOntimeCount: otp.arr.ontime,
    arrOTP,

    noOfPax: paxCount,
    loadFactor: paxCapacity && paxCount/paxCapacity,
    aircraft1day: {
      avg: safeAvg(aircraft1dayList),
      min: safeMin(aircraft1dayList),
      max: safeMax(aircraft1dayList),
    },
    flights1day: {
      avg: safeAvg(flights1dayList),
      min: safeMin(flights1dayList),
      max: safeMax(flights1dayList),
    },    
    blockHours1flight: {
      avg: safeAvg(blockHours1flightList),
      min: safeMin(blockHours1flightList),
      max: safeMax(blockHours1flightList),
    },
    pax1flight: {
      avg: safeAvg(pax1flightList),
      min: safeMin(pax1flightList),
      max: safeMax(pax1flightList),
    },    
    depOTP1day: {
      avg: depOTP,
      min: safeMin(depOTP1dayList),
      max: safeMax(depOTP1dayList),
    },
    arrOTP1day: {
      avg: arrOTP,
      min: safeMin(arrOTP1dayList),
      max: safeMax(arrOTP1dayList),
    },
    depDelaysTime: {
      avg: safeAvg(depDelaysTimeList),
      min: safeMin(depDelaysTimeList),
      max: safeMax(depDelaysTimeList),
    },
    arrDelaysTime: {
      avg: safeAvg(arrDelaysTimeList),
      min: safeMin(arrDelaysTimeList),
      max: safeMax(arrDelaysTimeList),
    },
    scheduleTat: {
      avg: safeAvg(scheduleTatList),
      min: safeMin(scheduleTatList),
      max: safeMax(scheduleTatList),
    },
    tat: {
      avg: safeAvg(tatList),
      min: safeMin(tatList),
      max: safeMax(tatList),
    },    
    taxiOut: {
      avg: safeAvg(taxiOutList),
      min: safeMin(taxiOutList),
      max: safeMax(taxiOutList),
    },
    taxiIn: {
      avg: safeAvg(taxiInList),
      min: safeMin(taxiInList),
      max: safeMax(taxiInList),
    },
  };
};


const Dashboard = () => {
  useAuthCheck();  
  const timeMode = useSelector((state) => state.timemode);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  const { startDate, endDate } = useDateRangeContext();
  const dateDiff = differenceInDays(endDate, startDate) + 1;
  
  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const flightsData = useSelector((state) => state.flights); 
  const crewRoutesData = useSelector((state) => state.crewRoutes); 
  
  const params = useSelector((state) => state.params);

  const [showFireworks, setShowFireworks] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  const [otpChartOpen, setOtpChartOpen] = useState(false);
  const [sortBarChartOpen, setSortBarChartOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [view, setView] = useState('flightsByFlightReg');
  const [crewPairings, setCrewPairings] = useState([]);
  const [otpDelayType, setOtpDelayType] = useState("departure");
  const [otpSelectedPorts, setOtpSelectedPorts] = useState([]);

  const EMPTY_STATS = { avg: 0, min: 0, max: 0 };
  const initIndicators = {
    aircraftCount: 0,
    flightsTotal: 0,
    flownFlightsCount: 0,
    blockTime: 0,
    flownBlockTime: 0,
    arrivedFlightsCount: 0,
    bh1AC1Day: 0,    
    depDelaysCount: 0,
    depOntimeCount: 0,
    depOTP: 100,
    arrDelaysCount: 0,
    arrOntimeCount: 0,
    arrOTP: 100,
    noOfPax: 0,
    loadFactor: 0,
    aircraft1day: EMPTY_STATS,
    flights1day: EMPTY_STATS,
    blockHours1flight: EMPTY_STATS,
    pax1flight: EMPTY_STATS,
    depOTP1day: EMPTY_STATS,
    arrOTP1day: EMPTY_STATS,
    depDelaysTime: EMPTY_STATS,
    arrDelaysTime: EMPTY_STATS,
    scheduleTat: EMPTY_STATS,
    tat: EMPTY_STATS,
    taxiOut: EMPTY_STATS,
    taxiIn: EMPTY_STATS,
  };
  
  const [indicators, setIndicators] = useState(initIndicators);  
  const [data, setData] = useState({
    flights: [],
    flightsByFlightReg: [],
    dailyFlightsByFlightReg: [],
    flightsByACType: [],
    flightsByFlightDate: [],
    flightsByDep: [],
    flightsBySector: [],
    flightsByFlightType: [],
    otpByETDRange: [],
  });


  const fetchHolidays = useCallback(async () => {
    const response = await api.get('/holidays/active');
    setHolidays(response.data || []);
  }, []);

  const fetchCrewPairings = useCallback(async () => {
    if (!startDate || !endDate) return;

    const url = '/flights/crewpairings';     
    const payload = { 
      startDate: addDays(startDate, -1), 
      endDate: addDays(endDate, 1) 
    };      
    const response = await api.post(url, payload);

    if (response.data) {
      const filteredRoutes = response.data?.filter(route => {
        const routeDateToCompare = timeMode === 'UTC'
          ? route.routeDate
          : route.routeDateLocalBase;

        return (
          compareAsc(routeDateToCompare, startDate) >= 0 &&
          compareAsc(routeDateToCompare, endDate) <= 0
        );
      });

      setCrewPairings(filteredRoutes);
    }
  }, [timeMode, startDate, endDate]);

  const HOME_BASES = params?.find((item) => item.param === "HOME_BASES")?.value || "SGN, HAN, DAD";
  useEffect(() => {
    const parsed = HOME_BASES.split(',').map(p => p.trim()).filter(Boolean);
    setOtpSelectedPorts(parsed);
  }, [HOME_BASES]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);

        await Promise.all([
          fetchHolidays(),
          fetchCrewPairings(),
        ]);                
        
      } catch (error) {
        const message = error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch data!";

        toast.error(message);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [      
    fetchHolidays,
    fetchCrewPairings,  
  ]);

  const isHolidayActiveToday = useMemo(() => {
    if (!holidays.length) return false;

    const today = new Date();
    const currentYear = today.getFullYear();

    return holidays.some(holiday => {
      if (!holiday.isActive) return false; // Skip inactive holidays

      let start = new Date(holiday.startDate);
      let end = holiday.endDate ? new Date(holiday.endDate) : start; // Default end to start if not provided

      // ===== LUNAR RANGE =====
      if (holiday.isLunar) {
        if (holiday.frequency === "yearly") {
          // yearly single day
          const solar = convertLunarToSolar(
            `${currentYear}-${format(start, "MM")}-${format(start, "dd")}`
          );
          return solar && isSameDay(today, solar);
        }

        // lunar range (once / yearly range)
        const range = convertLunarRangeToSolarByYear(
          start,
          end,
          currentYear
        );

        if (!range) return false;

        return today >= range.solarStart && today <= range.solarEnd;
      }
      
      // ===== SOLAR NORMAL =====
      switch (holiday.frequency) {
        case 'once':
          return isSameDay(today, start) || (start <= today && end >= today);
        case 'weekly':
          return today.getDay() === start.getDay(); // Check if today is the same day of week
        case 'monthly':
          return today.getDate() === start.getDate(); // Check if today is the same day of month
        case 'yearly':
          return today.getMonth() === start.getMonth() && today.getDate() === start.getDate(); // Check if same month and day
        default:
          return false;
      }
    });
  }, [holidays]);

  // Trigger fireworks based on holiday
  useEffect(() => {
    if (loading) return; 

    if (isHolidayActiveToday) {
      setShowFireworks(true);

      const timeout = setTimeout(() => {
        setShowFireworks(false);
      }, 9000);

      return () => clearTimeout(timeout); 
    } else {
      setShowFireworks(false); // Ensure fireworks are off if not a holiday
    }
  }, [isHolidayActiveToday, loading]);

  const flights = useMemo(() => 
    removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
  , [flightsData]);
  
  const nonDivFlights = useMemo(() => 
    flights.filter(f => !DIVERT_RETURN_STATUS?.includes(f.flightStatus))
  , [flights]);

  const debouncedFlights = useMemo(() => debounce(async () => {
    try {
      setLoading(true);
      const chartData = computeChartData(nonDivFlights, timeMode, view, dateDiff);
      setData({
        flights,
        otpByETDRange: calculateOTPByETDRange(nonDivFlights, timeMode),
        ...chartData,
      });
      
      setIndicators(calcIndicators(nonDivFlights, timeMode, dateDiff));
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to debounce flights!");
    } finally {
      setLoading(false);
    }
  }, 300), [view, flights, nonDivFlights, dateDiff, timeMode]);

  useEffect(() => {
    debouncedFlights();
    return () => debouncedFlights.cancel();
  }, [debouncedFlights]);
    
  const handleOpen = useCallback(() => {
    setChartOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setChartOpen(false);
  }, []);

  const handleOtpOpen = useCallback(() => setOtpChartOpen(true), []);
  const handleOtpClose = useCallback(() => setOtpChartOpen(false), []);

  const handleSortBarOpen = useCallback(() => setSortBarChartOpen(true), []);
  const handleSortBarClose = useCallback(() => setSortBarChartOpen(false), []);

  const handleViewChange = useCallback((e) => {
    setView(e.target.value);
  }, []);
  
  const styles = useMemo(() => ({
    borderRadius: 1,
    border: "1px solid",
    borderColor: "neutral.deep",
    backgroundColor: "background.default",
  }), []);

  const dialogStyles = useMemo(() => ({    
    height: LAYOUT_HEIGHT, 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    ...styles,
  }), [styles]);

  const chartMap = useMemo(() => ({
    flightsByFlightReg: { component: StackedBarChart, props: { bottomLegend: "Flight Reg", leftLegend: "Flights", bottomOffSet: -80, bottomLegendOffSet: 5 } },
    dailyFlightsByFlightReg: { component: BarLineChart, props: { bottomLegend: "Flight Reg", leftLegend: "Value", bottomOffSet: -80, bottomLegendOffSet: 5 } },
    flightsByACType: { component: PieChart, props: { itemWidthOffSet: isMobile ? -50 : -40, translateXOffSet: isMobile ? -15 : -10 } },
    flightsByFlightDate: { component: StackedBarChart, props: { bottomLegend: "Date", leftLegend: "Flights", bottomOffSet: -90, bottomLegendOffSet: -5 } },
    flightsByDep: { component: StackedBarChart, props: { bottomLegend: "Port", leftLegend: "Flights", bottomOffSet: -95, bottomLegendOffSet: -10 } },
    flightsBySector: { component: StackedBarChart, props: { bottomLegend: "Sector", leftLegend: "Flights", bottomOffSet: -75, bottomLegendOffSet: 10 } },
    flightsByFlightType: { component: PieChart, props: { itemWidthOffSet: 15 } },
  }), [isMobile]);
  
  const renderChart = useCallback((view, styles, props) => {
    const chart = chartMap[view];
    if (!chart) {
      return (
        <Box pl={1} sx={styles}>                                
          <Typography>No charts available</Typography>
        </Box>
      );
    }
    
    if (!data[view] || data[view].length === 0) {
      return (
        <FlexCenter sx={styles}>
          <CircularProgress size={30} />
        </FlexCenter>
      );
    }
    
    const ChartComponent = chart.component;
    return (
      <Box sx={{ ...styles }}>
        <ChartComponent data={data[view]} {...chart.props} {...props} />
      </Box>
    );
  }, [data, chartMap]);
   
  const stats = useMemo(() => [
    {
      icon: <AirplanemodeActive sx={{ color: "green.dark", fontSize: 24 }} />,      
      props: {
        "Aircraft": indicators.aircraftCount?.toLocaleString('en-US'),
        "BH/ AC/ Day": indicators.bh1AC1Day?.toFixed(1),
      }    
    },
    {
      icon: <FlightTakeoff sx={{ color: "green.dark", fontSize: 24 }} />,
      props: {
        title: "Flights",
        divisor: indicators.flownFlightsCount?.toLocaleString('en-US'),
        dividend: indicators.flightsTotal?.toLocaleString('en-US'),
        progress: indicators.flightsTotal && (indicators.flownFlightsCount / indicators.flightsTotal),
        rate: `${(indicators.flightsTotal && (indicators.flownFlightsCount / indicators.flightsTotal * 100).toFixed(0)) || 0}%`,
      }
    },
    {
      icon: <Timelapse sx={{ color: "green.dark", fontSize: 24 }} />,
      props: {
        title: "Block Hours",
        divisor: Number(indicators.flownBlockTime?.toFixed(0)).toLocaleString('en-US'),
        dividend: Number(indicators.blockTime?.toFixed(0)).toLocaleString('en-US'),
        progress: indicators.blockTime && (indicators.flownBlockTime / indicators.blockTime),
        rate: `${(indicators.blockTime && (indicators.flownBlockTime / indicators.blockTime * 100)?.toFixed(0)) || 0}%`,
      }
    },
    {
      icon: <FlightClass sx={{ color: "green.dark", fontSize: 24 }} />,
      props: {
        title: "Load Factor",
        divisor: `${(indicators.noOfPax / 1000)?.toFixed(0)}K`,
        dividend: indicators.capacity?.toLocaleString('en-US'),
        progress: indicators.loadFactor,
        rate: `${(indicators.loadFactor * 100 || 0)?.toFixed(1)}%`,
      }
    },
    {
      icon: <AccessTime sx={{ color: "green.dark", fontSize: 24 }} />,
      props: {
        title: "Dep OTP",
        divisor: indicators.depOntimeCount?.toLocaleString('en-US'),
        dividend: indicators.flownFlightsCount?.toLocaleString('en-US'),
        progress: indicators.depOTP / 100,
        rate: `${indicators.depOTP?.toFixed(1)}%`,
      }
    },
    {
      icon: <AccessTimeFilled sx={{ color: "green.dark", fontSize: 24 }} />,
      props: {
        title: "Arr OTP",
        divisor: indicators.arrOntimeCount?.toLocaleString('en-US'),
        dividend: indicators.arrivedFlightsCount?.toLocaleString('en-US'),
        progress: indicators.arrOTP / 100,
        rate: `${indicators.arrOTP?.toFixed(1)}%`,
      }
    },
  ], [indicators]);

  const indexes = useMemo(() => [
    {
      title: 'Daily Aircraft',
      index: indicators.aircraft1day.avg?.toFixed(1),
      description: 'Aircraft',
      props: [
        { label: 'H', value: indicators.aircraft1day.max?.toFixed(0), color: "info.main" },
        { label: 'L', value: indicators.aircraft1day.min?.toFixed(0), color: "error.main" }
      ]
    },
    {
      title: 'Daily Flights',
      index: indicators.flights1day.avg?.toFixed(0),
      description: 'Flights',
      props: [
        { label: 'H', value: indicators.flights1day.max?.toFixed(0), color: "info.main" },
        { label: 'L', value: indicators.flights1day.min?.toFixed(0), color: "error.main" }
      ]
    },
    {
      title: 'BH per Flight',
      index: indicators.blockHours1flight.avg?.toFixed(2),
      description: 'Hours',
      props: [
        { label: 'H', value: indicators.blockHours1flight.max?.toFixed(2), color: "info.main" },
        { label: 'L', value: indicators.blockHours1flight.min?.toFixed(2), color: "error.main" }
      ]
    },
    {
      title: 'Pax per Flight',
      index: indicators.pax1flight.avg?.toFixed(0),
      description: 'Pax',
      props: [
        { label: 'H', value: indicators.pax1flight.max?.toFixed(0), color: "info.main" },
        { label: 'L', value: indicators.pax1flight.min?.toFixed(0), color: "error.main" }
      ]
    },
    {
      title: 'Dep OTP',
      index: indicators.depOTP1day.avg?.toFixed(1),
      description: 'Percent',
      props: [
        { label: 'H', value: indicators.depOTP1day.max?.toFixed(0), color: "info.main" },
        { label: 'L', value: indicators.depOTP1day.min?.toFixed(0), color: "error.main" }
      ]
    },    
    {
      title: 'Arr OTP',
      index: indicators.arrOTP1day.avg?.toFixed(1),
      description: 'Percent',
      props: [
        { label: 'H', value: indicators.arrOTP1day.max?.toFixed(0), color: "info.main" },
        { label: 'L', value: indicators.arrOTP1day.min?.toFixed(0), color: "error.main" }
      ]
    },
    {
      title: 'Dep Delays',
      index: indicators.depDelaysTime.avg?.toFixed(0),
      description: 'Minutes',
      props: [
        { label: 'H', value: indicators.depDelaysTime.max?.toFixed(0), color: "error.main" },
        { label: 'L', value: indicators.depDelaysTime.min?.toFixed(0), color: "info.main" }
      ]
    },
    {
      title: 'Arr Delays',
      index: indicators.arrDelaysTime.avg?.toFixed(0),
      description: 'Minutes',
      props: [
        { label: 'H', value: indicators.arrDelaysTime.max?.toFixed(0), color: "error.main" },
        { label: 'L', value: indicators.arrDelaysTime.min?.toFixed(0), color: "info.main" }
      ]
    },
    {
      title: 'Schedule TAT',
      index: indicators.scheduleTat.avg?.toFixed(0),
      description: 'Minutes',
      props: [
        { label: 'H', value: indicators.scheduleTat.max?.toFixed(0), color: "error.main" },
        { label: 'L', value: indicators.scheduleTat.min?.toFixed(0), color: "info.main" }
      ]
    },
    {
      title: 'Actual TAT',
      index: indicators.tat.avg?.toFixed(0),
      description: 'Minutes',
      props: [
        { label: 'H', value: indicators.tat.max?.toFixed(0), color: "error.main" },
        { label: 'L', value: indicators.tat.min?.toFixed(0), color: "info.main" }
      ]
    },
    {
      title: 'Taxi Out',
      index: indicators.taxiOut.avg?.toFixed(0),
      description: 'Minutes',
      props: [
        { label: 'H', value: indicators.taxiOut.max?.toFixed(0), color: "error.main" },
        { label: 'L', value: indicators.taxiOut.min?.toFixed(0), color: "info.main" }
      ]
    },
    {
      title: 'Taxi In',
      index: indicators.taxiIn.avg?.toFixed(0),
      description: 'Minutes',
      props: [
        { label: 'H', value: indicators.taxiIn.max?.toFixed(0), color: "error.main" },
        { label: 'L', value: indicators.taxiIn.min?.toFixed(0), color: "info.main" }
      ]
    },    
  ], [indicators]);
 

  return (
    <Box className="main-container-box">      
      <FlexBetween>
        <Header 
          title="Dashboard"  
          subtitle={`${loading ? 'Loading...' : 'Welcome to the dashboard'}`}
        />  
        {/* <Box sx={{ mr: 1.5 }}>
          <AnalogClock />        
        </Box>      */}
      </FlexBetween>              
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}  
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ overflowY: 'auto', pr: !isMobile ? 0.5 : 0 }}
        >  
          {/* ROW 1 */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={100}
            gap={2}           
            sx={{ "& > div": { gridColumn: isDesktop ? "span 2" 
              : isTablet ? "span 4" 
                : isMobile ? "span 6" : "span 4" } 
            }}
          >             
            {stats?.map((item, index) => (
              <StatBox
                key={index}
                icon={item.icon}
                props={item.props}
              />
            ))}            
          </Box>

          {/* ROW 2 */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={100}
            gap={2}
          >
            {/* LEFT – Index Boxes */}
            <Box
              gridColumn={isDesktop ? "span 6"
                : isTablet ? "span 12" : "span 12"
              }
              gridRow={isDesktop ? "span 4"
                : isTablet ? "span 4"
                  : isMobile ? "span 6" : "auto"
              }
              display="grid"                               
              gridTemplateColumns={isDesktop
                  ? "repeat(3, 1fr)"   // 4 cột
                  : isTablet
                    ? "repeat(3, 1fr)" // 3 cột
                    : "repeat(2, 1fr)" // 2 cột
              }
              gap={2}
            >
              {indexes?.map((item, index) => (
                <IndexBox
                  key={item.id ?? index}
                  title={item.title}
                  index={item.index}
                  description={item.description}
                  props={item.props}
                />
              ))}
            </Box>

            {/* RIGHT – Flight Delay List */}
            <Box
              gridColumn={
                isDesktop ? "span 6"
                : isTablet ? "span 12"
                : "span 12"
              }
              gridRow={
                isDesktop ? "span 4"
                : isTablet ? "span 4"
                : isMobile ? "span 4" : "auto"
              }
              overflow="auto"
              sx={styles}
            >
              <FlightDelayList flights={data?.flights} />
            </Box>
          </Box>
                    
          {/* ROW 3 */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={110}
            gap={2}
          >
            {/* Chart Section */}
            <Box
              display="flex"
              flexDirection="column"
              gridColumn="span 12"
              gridRow="span 4"
              sx={styles}
            >
              {/* Dropdown Menu */}
              <Box m={1}>
                <FormControl size="small">
                  <InputLabel>View</InputLabel>
                  <Select
                    variant="outlined"                    
                    label="View"
                    value={view}
                    onChange={handleViewChange}
                  >
                    {VIEW_OPTIONS.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Chart Display */}
              <Box
                flex={1}
                overflow="hidden"
                onDoubleClick={() => handleOpen(view, data[view])}
              >
                {renderChart(view, { height: "100%" }, { })}
              </Box>
            </Box>
          </Box>
          
          {/* ROW 4 */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={110}
            gap={2}
          >
            <Box
              display="flex"
              flexDirection="column"
              gridColumn="span 12"
              gridRow={isMobile ? "span 5" : "span 4"}
              onDoubleClick={handleOtpOpen}
              sx={styles}
            >
              <OtpByPorts
                flights={nonDivFlights}
                delayType={otpDelayType}
                onDelayTypeChange={setOtpDelayType}
                selectedPorts={otpSelectedPorts}
                onSelectedPortsChange={setOtpSelectedPorts}
              />
            </Box>
          </Box>
          
          {/* ROW 5 */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={110}
            gap={2}
          >               
            <Box
              gridColumn={
                isDesktop ? "span 4"
                : isTablet ? "span 12"
                : "span 12"
              }
              gridRow="span 4"
              overflow="auto"
              sx={styles}
            >
              <ShortTurnaroundCrewRoutes 
                crewRoutes={crewPairings || crewRoutesData} 
              />
            </Box>

            <Box
              gridColumn={
                isDesktop ? "span 8"
                : isTablet ? "span 12"
                : "span 12"
              }
              gridRow="span 4"
              onDoubleClick={handleSortBarOpen}
              sx={styles}
            >
              <SortBarMultiLinesChart
                data={data?.otpByETDRange}
                title={`OTP by Etd Range`}
                isSortVisible={true}
                bottomLegend='Oclock'
                leftLegend='OTP (%)'
                digit={isDesktop ? 1 : 0}
                bottomOffSet={-10}
                bottomLegendOffSet={-10}
                barTranslateXOffSet={110}
                lineTranslateXOffSet={-10}
              />
            </Box>
          </Box> 

          <PageFooter />
          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      )}    

      {showFireworks && <FireworksComponent />}
      {chartOpen && (
        <ChartDialog
          open={chartOpen}
          onClose={handleClose}
          chartComponent={renderChart(view, { ...dialogStyles, pt: 3 }, { })}
        />
      )}
      {otpChartOpen && (
        <ChartDialog
          open={otpChartOpen}
          onClose={handleOtpClose}
          chartComponent={
            <Box sx={dialogStyles}>
              <OtpByPorts
                flights={nonDivFlights}
                delayType={otpDelayType}
                onDelayTypeChange={setOtpDelayType}
                selectedPorts={otpSelectedPorts}
                onSelectedPortsChange={setOtpSelectedPorts}
              />
            </Box>
          }
        />
      )}
      {sortBarChartOpen && (
        <ChartDialog
          open={sortBarChartOpen}
          onClose={handleSortBarClose}
          chartComponent={
            <Box sx={dialogStyles}>
              <SortBarMultiLinesChart
                data={data?.otpByETDRange}
                title="OTP by Etd Range"
                isSortVisible={true}
                bottomLegend='Oclock'
                leftLegend='OTP (%)'
                digit={1}
                bottomOffSet={-10}
                bottomLegendOffSet={-10}
                barTranslateXOffSet={110}
                lineTranslateXOffSet={-10}
              />
            </Box>
          }
        />
      )}
    </Box>
  );
};

export default Dashboard;
